import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  MagnifyingGlassIcon, 
  GlobeAltIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  GlobeAmericasIcon,
  MapIcon,
  BuildingLibraryIcon,
  ArrowsRightLeftIcon,
  ArrowsUpDownIcon,
  DocumentTextIcon,
  HomeIcon,
  Square3Stack3DIcon,
  RectangleGroupIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface IPInfo {
  addr: string;
  as: string;
  city: string;
  country: string;
  district: string;
  ip: string;
  latitude: number;
  longitude: number;
  province: string;
  registered_country: string;
  subdivisions: string[];
}

// API响应包装器接口
interface APIResponse<T> {
  code: number;
  message: string;
  data: T;
}

// IP地址验证正则表达式
const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

// 配置检查和创建axios实例
const initializeAPI = () => {
  // 创建axios实例
  const instance = axios.create({
    baseURL: '/api',  // 使用/api作为基础URL
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    timeout: 30000, // 增加超时时间到30秒
    withCredentials: false
  });

  // 添加响应拦截器
  instance.interceptors.response.use(
    response => response,
    error => {
      console.log('Error details:', error); // 添加错误日志
      
      // 统一处理错误，不暴露具体错误信息
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 429) {
          return Promise.reject(new Error('请求过于频繁，请稍后重试'));
        } else if (error.response?.status === 404) {
          return Promise.reject(new Error('未找到该IP信息'));
        } else if (error.response?.status === 400) {
          return Promise.reject(new Error('请输入有效的IP地址'));
        } else if (error.response?.status === 401) {
          return Promise.reject(new Error('服务认证失败，请稍后重试'));
        } else if (error.code === 'ECONNABORTED') {
          return Promise.reject(new Error('请求超时，请检查网络连接'));
        } else if (error.message.includes('Network Error')) {
          return Promise.reject(new Error('网络连接失败，请检查网络设置'));
        }
      }
      return Promise.reject(new Error('查询失败，请稍后重试'));
    }
  );

  return instance;
};

const axiosInstance = initializeAPI();

interface RequestRecord {
  timestamp: number;
}

// 速率限制配置
const RATE_LIMIT = {
  MAX_TOKENS: 3,        // 令牌桶容量（最多允许3个请求）
  REFILL_RATE: 0.2,     // 每秒补充0.2个令牌
  REFILL_INTERVAL: 1000 // 每1秒检查并补充令牌
};

// 令牌桶接口
interface TokenBucket {
  tokens: number;
  lastRefill: number;
}

// 速率限制提示组件
function RateLimitAlert({ countdown }: { countdown: number }) {
  return (
    <div className="absolute left-0 right-0 -top-14 flex items-center gap-2 bg-amber-50 text-amber-800 px-3 py-2 rounded-lg border border-amber-200 shadow-sm">
      <div className="shrink-0">
        <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <div className="text-sm">
        请求过于频繁，请等待 <span className="font-medium">{countdown}</span> 秒后重试
      </div>
    </div>
  );
}

// 全局变量，用于追踪是否已经发起过初始请求
let hasInitialFetchGlobal = false;

// 错误提示组件
function ErrorAlert({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <div className="absolute left-0 right-0 -top-14 flex items-center justify-between gap-2 bg-red-50 text-red-800 px-3 py-2 rounded-lg border border-red-200 shadow-sm animate-fade-in">
      <div className="flex items-center gap-2">
        <ExclamationCircleIcon className="w-5 h-5 text-red-500 shrink-0" />
        <div className="text-sm">{message}</div>
      </div>
      <button
        onClick={onClose}
        className="text-red-500 hover:text-red-700 transition-colors"
      >
        <XMarkIcon className="w-5 h-5" />
      </button>
    </div>
  );
}

function App() {
  const [ipAddress, setIpAddress] = useState('');
  const [ipInfo, setIpInfo] = useState<IPInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState<number>(0);
  const [tokenBucket, setTokenBucket] = useState<TokenBucket>({
    tokens: RATE_LIMIT.MAX_TOKENS,
    lastRefill: Date.now()
  });

  // 组件加载时获取当前IP信息
  useEffect(() => {
    const initialFetch = async () => {
      // 使用全局变量检查是否已经发起过初始请求
      if (hasInitialFetchGlobal) {
        return;
      }
      
      try {
        setLoading(true);
        hasInitialFetchGlobal = true;
        
        const response = await axiosInstance.get<APIResponse<IPInfo>>('/ip');
        
        if (response.status === 200 && response.data.code === 0) {
          setIpInfo({
            ...response.data.data,
            subdivisions: response.data.data.subdivisions || []
          });
        }
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('查询失败');
        }
        setIpInfo(null);
      } finally {
        setLoading(false);
      }
    };

    initialFetch();
  }, []);

  // 处理令牌桶补充
  useEffect(() => {
    const refillTimer = setInterval(() => {
      setTokenBucket(prev => {
        const now = Date.now();
        const timePassed = now - prev.lastRefill;
        const tokensToAdd = Math.floor(timePassed / RATE_LIMIT.REFILL_INTERVAL) * RATE_LIMIT.REFILL_RATE;
        
        if (tokensToAdd > 0) {
          return {
            tokens: Math.min(RATE_LIMIT.MAX_TOKENS, prev.tokens + tokensToAdd),
            lastRefill: now
          };
        }
        return prev;
      });
    }, RATE_LIMIT.REFILL_INTERVAL);

    return () => clearInterval(refillTimer);
  }, []);

  // 处理倒计时
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            setError('');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [countdown]);

  const validateIP = (ip: string): boolean => {
    return ipRegex.test(ip);
  };

  const fetchIPInfo = useCallback(async (targetIP?: string) => {
    try {
      setLoading(true);
      setError('');
      
      // 修改请求路径
      const endpoint = targetIP ? `/ip/${targetIP}` : '/ip';
      console.log('开始请求:', endpoint);
      
      const response = await axiosInstance.get<APIResponse<IPInfo>>(endpoint);
      console.log('请求响应:', response.data);
      
      if (response.status === 200 && response.data.code === 0) {
        const ipData = {
          ...response.data.data,
          subdivisions: response.data.data.subdivisions || []
        };
        setIpInfo(ipData);
        setError('');
      } else {
        console.log('请求返回非0状态码:', response.data);
        throw new Error(response.data.message || '查询失败，请稍后重试');
      }
    } catch (err) {
      console.error('请求出错:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('查询失败，请稍后重试');
      }
      setIpInfo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // 检查是否可以发送请求
  const canMakeRequest = useCallback((): boolean => {
    if (tokenBucket.tokens < 1) {
      const waitTime = Math.ceil((1 - tokenBucket.tokens) / RATE_LIMIT.REFILL_RATE);
      setError(`请求过于频繁，请等待${waitTime}秒后重试`);
      setCountdown(waitTime);
      return false;
    }
    return true;
  }, [tokenBucket.tokens]);

  // 消耗令牌
  const consumeToken = useCallback(() => {
    setTokenBucket(prev => ({
      ...prev,
      tokens: Math.max(0, prev.tokens - 1)
    }));
  }, []);

  // 获取输入框placeholder文本
  const getPlaceholder = useCallback(() => {
    if (loading) {
      return '查询中...';
    }
    if (countdown > 0) {
      return `请求过快，请等待${countdown}秒后重试...`;
    }
    if (error) {
      return error;
    }
    return '请输入IP地址（例如：8.8.8.8）';
  }, [countdown, error, loading]);

  // IP地址错误提示组件
  const InvalidIPAlert = () => (
    <div className="absolute left-0 right-0 -top-14 flex items-center gap-2 bg-red-50 text-red-800 px-3 py-2 rounded-lg border border-red-200 shadow-sm">
      <div className="shrink-0">
        <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
        </svg>
      </div>
      <div className="text-sm">
        请输入有效的IP地址格式（例如：8.8.8.8）
      </div>
    </div>
  );

  // 处理搜索
  const handleSearch = async () => {
    if (loading || countdown > 0) {
      return;
    }

    if (!ipAddress) {
      setError('请输入IP地址');
      return;
    }

    if (!validateIP(ipAddress)) {
      setError('请输入有效的IP地址格式（例如：8.8.8.8）');
      return;
    }

    if (!canMakeRequest()) {
      return;
    }

    consumeToken();
    await fetchIPInfo(ipAddress);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !loading && countdown === 0) {
      e.preventDefault();
      handleSearch();
    }
  };

  // 处理错误关闭
  const handleErrorClose = () => {
    setError('');
  };

  return (
    <div className="min-h-screen p-2 sm:p-6 lg:p-8 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-2xl sm:max-w-3xl lg:max-w-4xl mx-auto">
        <div className="text-center mb-4 sm:mb-6">
          <div className="inline-flex items-center justify-center mb-1 sm:mb-2">
            <GlobeAltIcon className="w-6 h-6 sm:w-8 sm:h-8 text-gray-700" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">IP地址查询</h1>
          <p className="text-xs sm:text-sm text-gray-600">输入IP地址，快速获取详细地理位置信息</p>
        </div>
        
        <div className="bg-white rounded-xl shadow-xl p-4 sm:p-6 backdrop-blur-sm bg-opacity-95">
          <div className="flex gap-2 relative">
            {countdown > 0 && <RateLimitAlert countdown={countdown} />}
            {error && <ErrorAlert message={error} onClose={handleErrorClose} />}
            <div className="relative flex-1">
              <input
                type="text"
                value={ipAddress}
                onChange={(e) => setIpAddress(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={getPlaceholder()}
                className={`w-full h-9 sm:h-10 px-3 sm:px-4 border rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-transparent transition-all duration-200 text-gray-700 placeholder-gray-400 text-sm ${
                  countdown > 0 ? 'border-amber-300 bg-amber-50' : 
                  error ? 'border-red-300 bg-red-50' :
                  ipAddress && !validateIP(ipAddress) ? 'border-red-300 bg-red-50' : 
                  'border-gray-300'
                }`}
                disabled={loading || countdown > 0}
              />
              {ipAddress && !loading && (
                <div className={`absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-sm ${
                  validateIP(ipAddress) ? 'text-green-500' : 'text-red-500'
                }`}>
                  {validateIP(ipAddress) ? '✓' : '✗'}
                </div>
              )}
            </div>
            <button
              onClick={() => !loading && countdown === 0 && handleSearch()}
              disabled={Boolean(loading || countdown > 0 || (ipAddress && !validateIP(ipAddress)))}
              className={`h-9 sm:h-10 px-3 sm:px-4 text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-500 flex items-center justify-center disabled:cursor-not-allowed transition-all duration-200 text-sm relative min-w-[90px] sm:min-w-[100px] whitespace-nowrap ${
                countdown > 0 ? 'bg-amber-500 disabled:opacity-90' : 
                error ? 'bg-red-500 disabled:opacity-90' :
                ipAddress && !validateIP(ipAddress) ? 'bg-red-500 disabled:opacity-90' :
                'bg-gray-900 hover:bg-gray-800 disabled:opacity-50'
              }`}
            >
              {loading ? (
                <div className="flex items-center gap-1.5">
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                  <span>查询中</span>
                </div>
              ) : countdown > 0 ? (
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 animate-pulse" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{countdown}s</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <MagnifyingGlassIcon className="w-4 h-4" />
                  <span>查询</span>
                </div>
              )}
            </button>
          </div>

          {ipInfo && (
            <div className="mt-4 sm:mt-6">
              <h2 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3">查询结果</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                <InfoItem 
                  label="IP地址" 
                  value={ipInfo.ip}
                  icon={<GlobeAltIcon className="w-4 h-4 text-gray-500" />}
                />
                <InfoItem 
                  label="国家/地区" 
                  value={ipInfo.country}
                  icon={<GlobeAmericasIcon className="w-4 h-4 text-gray-500" />}
                />
                <InfoItem 
                  label="省份" 
                  value={ipInfo.province}
                  icon={<MapIcon className="w-4 h-4 text-gray-500" />}
                />
                <InfoItem 
                  label="城市" 
                  value={ipInfo.city}
                  icon={<BuildingOfficeIcon className="w-4 h-4 text-gray-500" />}
                />
                <InfoItem 
                  label="区域" 
                  value={ipInfo.district || '-'}
                  icon={<MapPinIcon className="w-4 h-4 text-gray-500" />}
                />
                <InfoItem 
                  label="运营商" 
                  value={ipInfo.as}
                  icon={<BuildingLibraryIcon className="w-4 h-4 text-gray-500" />}
                />
                <InfoItem 
                  label="经度" 
                  value={String(ipInfo.longitude)}
                  icon={<ArrowsRightLeftIcon className="w-4 h-4 text-gray-500" />}
                />
                <InfoItem 
                  label="纬度" 
                  value={String(ipInfo.latitude)}
                  icon={<ArrowsUpDownIcon className="w-4 h-4 text-gray-500" />}
                />
                <InfoItem 
                  label="注册国家" 
                  value={ipInfo.registered_country}
                  icon={<DocumentTextIcon className="w-4 h-4 text-gray-500" />}
                />
                <InfoItem 
                  label="地址" 
                  value={ipInfo.addr}
                  icon={<HomeIcon className="w-4 h-4 text-gray-500" />}
                />
                <InfoItem 
                  label="地址范围" 
                  value={ipInfo.addr}
                  icon={<RectangleGroupIcon className="w-4 h-4 text-gray-500" />}
                />
                {ipInfo.subdivisions?.length > 0 && (
                  <InfoItem 
                    label="行政区划" 
                    value={ipInfo.subdivisions.join(', ')}
                    icon={<Square3Stack3DIcon className="w-4 h-4 text-gray-500" />}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        <footer className="mt-3 sm:mt-4 text-center text-gray-500 text-xs sm:text-sm">
          <p>© {new Date().getFullYear()} IP地址查询服务</p>
        </footer>
      </div>
    </div>
  );
}

function InfoItem({ 
  label, 
  value, 
  icon 
}: { 
  label: string; 
  value: string; 
  icon: React.ReactNode;
}) {
  return (
    <div className="p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-200">
      <div className="flex items-center gap-1.5">
        {icon}
        <div className="text-xs text-gray-500">{label}</div>
      </div>
      <div className="text-sm text-gray-900 break-all mt-0.5 pl-5.5">{value || '-'}</div>
    </div>
  );
}

export default App; 